/**
 * Invoice detail, rebuilt in the order detail's visual language on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-details.tsx
 *   the document itself, behind the "Tax invoice" tab, through `InvoiceDocument`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid, the lines followed by the balance, the side column's cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `PageHero`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the four figure cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the parties and compliance cards.
 *
 * Rules this screen keeps:
 * - Every figure is the API's (constitution I) — the totals card prints Billing's fields as sent.
 * - The statutory layout is still `InvoiceDocument` and nothing else (FR-011); the line table here is
 *   a working view of the same lines, not a second rendering of the legal document.
 * - Cancel still goes through `?cancel=1` + `ReasonDialog`, so the list's row menu keeps working, and
 *   a window-passed refusal still lands on `?refused=window-passed` with the credit-note route.
 * - The audit trail is not awaited: the invoice renders the moment it arrives, and a trail that
 *   fails to read says so in its own card (constitution VI). Nor is the settlement (D-41).
 * - D-40: an invoice held for review says so at the top, and a Super Admin (`billing.review`) edits
 *   its discount and round off (`?act=revise`) or finalises it there. Every figure after an edit is
 *   Billing's answer, re-read — this screen computes none of them.
 * - D-41: once final, a Super Admin (`billing.settle`) records a settlement discount and round off
 *   after payment (`?act=settle`). The invoice and its GST do not change; the ledger does.
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  PiCalendarBlankDuotone as CalendarIcon,
  PiCaretDownBold as CaretDown,
  PiCheckCircleDuotone as FinaliseIcon,
  PiHandCoinsDuotone as SettleIcon,
  PiPencilSimpleLineDuotone as ReviseIcon,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiFileTextDuotone as CreditNoteIcon,
  PiInfoDuotone as InfoIcon,
  PiListChecksDuotone as LinesIcon,
  PiMapPinDuotone as MapPinIcon,
  PiPackageDuotone as PackageIcon,
  PiPrinterDuotone as PrintIcon,
  PiReceiptDuotone as ReceiptIcon,
  PiScalesDuotone as ScalesIcon,
  PiScrollDuotone as AuditIcon,
  PiSealCheckDuotone as SealIcon,
  PiShieldCheckDuotone as ShieldIcon,
  PiTruckDuotone as TruckIcon,
  PiUsersThreeDuotone as PartiesIcon,
  PiWarningDuotone as WarningIcon,
  PiXCircleDuotone as CancelIcon,
} from "react-icons/pi";
import toast from "react-hot-toast";
import { Link, useLoaderData, useNavigate, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { Alert, Avatar, Badge, Button, Dropdown, Tab, Table, Text } from "rizzui";
import { GRID_TABLE } from "../../components/table/table-view";
import { EmptyState, Field, InvoiceDocument, MetricCard, ReasonDialog, WidgetCard, getStatusBadge, type ComplianceState } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { useConfirm } from "../../components/confirm-modal";
import { CardTitle, CopyButton, InfoBlock, PageHero, StatusPill, iconMenuTriggerClass, menuTriggerClass } from "../../components/detail-parts";
import { AuditFeed, ComplianceBadge, PartyBlock, TotalsCard } from "../../components/invoicing/parts";
import { TileBoundary } from "../../components/dashboard/TileBoundary";
import { problemMessage } from "../../components/dashboard/tone";
import { formatDate } from "../../lib/format";
import { stateName } from "../../lib/invoice-model";
import { can } from "../../lib/permissions";
import { dispatchPath, invoicePath, orderPath } from "../../lib/paths";
import {
  BASIS_LABEL,
  BASIS_RULE,
  IRN_STATE,
  cancelInvoice,
  finaliseInvoice,
  getInvoiceAudit,
  invoiceByKey,
  getSettlement,
  reviseInvoice,
  settleInvoice,
  type Settlement,
  isCancelled,
  money,
  statusWords,
  windowPassed,
  type AuditEntry,
  type InvoiceDetail,
} from "../../lib/services/invoicing";
import type { ApiError } from "../../lib/api";
import type { AccountSummary, PermissionState } from "../../types";

export async function invoiceDetailLoader({ params, request }: LoaderFunctionArgs) {
  const invoice = await invoiceByKey(request, params.invoiceNumber!);
  const id = invoice.id;
  // Deliberately not awaited — see the file header. `TileBoundary` resolves it on its own.
  // `key` is the URL's own: the number, or the id where the number is ambiguous (then the id URL
  // is this invoice's only address, so its own links must keep it).
  return { invoice, id, key: params.invoiceNumber!, audit: getInvoiceAudit(id), settlement: getSettlement(id) };
}

export default function InvoiceDetailPage() {
  const { invoice: data, id, key, audit, settlement } = useLoaderData() as {
    invoice: InvoiceDetail;
    id: string;
    key: string;
    audit: Promise<AuditEntry[]>;
    settlement: Promise<Settlement | null>;
  };
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();

  const cancel = searchParams.get("cancel");
  const refused = searchParams.get("refused");
  const closedAt = searchParams.get("closedAt");

  const [dialogError, setDialogError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (cancel === "1") setDialogError(undefined);
  }, [cancel]);

  const cancelled = isCancelled(data.state);
  const mayCancel = can(permissionState, "billing.creditnote");
  const mayRegister = can(permissionState, "billing.administer");
  const mayDispatch = can(permissionState, "dispatch.assign");
  const mayReadRegistration = mayRegister || can(permissionState, "billing.read");
  const irnState: ComplianceState | undefined = IRN_STATE[data.irnStatus];
  const href = invoicePath(key);
  // The create form preselects its invoice by id — a query parameter, not the record's URL.
  const creditNoteHref = `/invoicing/credit-notes?invoiceId=${id}`;

  // D-40 / D-41. Who may act is the matrix's (Super Admin unless granted); what is on offer follows
  // the invoice's own state, and Billing refuses anything else with its own reason.
  const act = searchParams.get("act");
  const inReview = data.reviewPending && !cancelled;
  const mayReview = can(permissionState, "billing.review") && inReview;
  const maySettle =
    can(permissionState, "billing.settle") && !data.reviewPending && !cancelled && data.invoiceBasis === "actual_measured_quantity";
  const confirm = useConfirm();
  const [adjustError, setAdjustError] = useState<string | undefined>(undefined);
  const closeAct = () => {
    setAdjustError(undefined);
    navigate(href);
  };

  async function adjust(work: () => Promise<unknown>, done: string) {
    setAdjustError(undefined);
    try {
      await work();
      toast.success(done);
      // Leaving `?act=` re-runs the loader; an action taken from the page itself (Finalise) has no
      // URL to leave, so it revalidates instead. Doing both cancelled the reload.
      if (act) closeAct();
      else revalidator.revalidate();
    } catch (err) {
      const message = (err as ApiError).detail ?? (err as ApiError).title ?? "Infra Junction refused that change.";
      setAdjustError(message);
      toast.error(message);
    }
  }

  function handleRevise(fd: FormData) {
    const mode = String(fd.get("discountMode") ?? "none") as "none" | "percent" | "amount";
    const value = Number(String(fd.get("discountValue") ?? "0").trim() || "0");
    const roundOffText = String(fd.get("roundOff") ?? "").trim();
    const reason = String(fd.get("reason") ?? "").trim();
    if (reason === "") return setAdjustError("A reason is required.");
    if (!Number.isFinite(value) || (roundOffText !== "" && !Number.isFinite(Number(roundOffText)))) {
      return setAdjustError("Enter the discount and round off as numbers.");
    }
    void adjust(
      () => reviseInvoice(id, { discountMode: mode, discountValue: value, roundOff: roundOffText === "" ? null : Number(roundOffText), reason }),
      "Invoice re-priced",
    );
  }

  function handleSettle(fd: FormData) {
    const discount = Number(String(fd.get("discount") ?? "0").trim() || "0");
    const roundOff = Number(String(fd.get("roundOff") ?? "0").trim() || "0");
    const reason = String(fd.get("reason") ?? "").trim();
    if (reason === "") return setAdjustError("A reason is required.");
    if (!Number.isFinite(discount) || !Number.isFinite(roundOff)) return setAdjustError("Enter the discount and round off as numbers.");
    void adjust(() => settleInvoice(id, { discount, roundOff, reason }), "Settlement recorded");
  }

  const finalise = () =>
    confirm({
      title: `Finalise ${data.invoiceNumber}?`,
      consequence:
        "The invoice is released as it stands now: registered for an IRN where e-invoicing applies, queued for Tally, and posted to the customer's ledger. Its discount and round off can no longer be changed — a later reduction is a credit note or a settlement.",
      confirmLabel: "Finalise the invoice",
      onConfirm: () => void adjust(() => finaliseInvoice(id), `${data.invoiceNumber} finalised`),
    });

  async function handleCancel(formData: FormData) {
    const reason = String(formData.get("reason") ?? "").trim();
    if (reason === "") return setDialogError("A reason is required.");

    try {
      await cancelInvoice(id, reason);
      toast.success("Invoice cancelled");
      revalidator.revalidate();
      navigate(href);
    } catch (err) {
      const passed = windowPassed(err);
      if (passed) {
        const q = passed.closedAt ? `&closedAt=${encodeURIComponent(passed.closedAt)}` : "";
        navigate(`${href}?refused=window-passed${q}`);
        return;
      }
      const message = (err as ApiError).detail ?? (err as ApiError).title ?? "The cancellation was refused.";
      setDialogError(message);
      toast.error(message);
    }
  }

  return (
    <div className="@container flex flex-col gap-6">
      {/* ── Hero ─────────────────────────────────────────────────────────────────────────── */}
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: data.invoiceNumber }]}
        back={{ href: "/invoicing", label: "All invoices" }}
        icon={<ReceiptIcon aria-hidden />}
        title={data.invoiceNumber}
        copyValue={data.invoiceNumber}
        meta={
          <>
            <StatusPill status={data.state} />
            {inReview ? (
              <Badge variant="flat" color="warning" rounded="pill" className="gap-1.5">
                <ReviseIcon aria-hidden className="size-4" />
                Draft · awaiting review
              </Badge>
            ) : null}
            <ComplianceBadge subject="irn" status={data.irnStatus} map={IRN_STATE} />
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <ScalesIcon aria-hidden className="size-4" />
              {BASIS_LABEL[data.invoiceBasis] ?? statusWords(data.invoiceBasis)}
            </Badge>
            {data.discount ? (
              <Badge variant="flat" color="success" rounded="pill">
                {String(data.lines.find((line) => line.discountPercent)?.discountPercent ?? "")}% trade discount
              </Badge>
            ) : null}
            <Text as="span" className="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <CalendarIcon aria-hidden className="size-4" />
              Issued {formatDate(data.invoiceDate)} · FY {data.financialYear}
            </Text>
            <OrderLink invoice={data} />
          </>
        }
        actions={
          <>
            <Link to={`${href}/print`} target="_blank" rel="noopener">
              <Button as="span">
                <PrintIcon aria-hidden className="me-1.5 size-4" />
                Print
              </Button>
            </Link>
            <Dropdown placement="bottom-end">
              <Dropdown.Trigger aria-label={`Compliance and records for ${data.invoiceNumber}`} className={menuTriggerClass}>
                <ShieldIcon aria-hidden className="size-4 text-secondary" />
                Compliance
                <CaretDown aria-hidden className="size-3 text-gray-400" />
              </Dropdown.Trigger>
              <Dropdown.Menu className="w-64">
                {mayReadRegistration ? (
                  <Dropdown.Item onClick={() => navigate(`${href}/registration`)} className="gap-2.5 text-sm">
                    <SealIcon aria-hidden className="size-4 shrink-0 text-secondary" />
                    E-invoice registration
                  </Dropdown.Item>
                ) : null}
                {mayDispatch && !cancelled && !data.reviewPending ? (
                  <Dropdown.Item onClick={() => navigate(dispatchPath(key))} className="gap-2.5 text-sm">
                    <TruckIcon aria-hidden className="size-4 shrink-0 text-secondary" />
                    Dispatch / e-way bill
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item onClick={() => navigate(`${href}/audit`)} className="gap-2.5 text-sm">
                  <AuditIcon aria-hidden className="size-4 shrink-0 text-secondary" />
                  Audit trail
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            {mayCancel && !cancelled ? (
              <Dropdown placement="bottom-end">
                <Dropdown.Trigger aria-label={`More actions for ${data.invoiceNumber}`} className={iconMenuTriggerClass}>
                  <MoreIcon aria-hidden className="size-4" />
                </Dropdown.Trigger>
                <Dropdown.Menu className="w-56">
                  {!data.reviewPending ? (
                    <>
                      <Dropdown.Item onClick={() => navigate(creditNoteHref)} className="gap-2.5 text-sm">
                        <CreditNoteIcon aria-hidden className="size-4 shrink-0 text-secondary" />
                        Raise credit note
                      </Dropdown.Item>
                      <div role="separator" className="my-1 border-t border-muted" />
                    </>
                  ) : null}
                  <Dropdown.Item onClick={() => navigate(`${href}?cancel=1`)} className="gap-2.5 text-sm text-red-dark">
                    <CancelIcon aria-hidden className="size-4 shrink-0" />
                    Cancel invoice…
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </>
        }
      />

      {/* ── D-40: held for review ───────────────────────────────────────────────────────── */}
      {inReview ? (
        <Alert color="warning" variant="flat" bar icon={<ReviseIcon aria-hidden className="size-5" />}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="max-w-3xl">
              <Text className="font-semibold text-orange-dark">Draft — awaiting Super Admin review</Text>
              <Text className="mt-0.5 text-sm">
                Not yet registered for an IRN, sent to Tally, posted to the customer&rsquo;s ledger or shown to the customer. Its
                discount and round off can still change; finalising releases it and locks it.
              </Text>
            </div>
            {mayReview ? (
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => navigate(`${href}?act=revise`)}>
                  <ReviseIcon aria-hidden className="me-1.5 size-4" />
                  Edit discount &amp; round off
                </Button>
                <Button type="button" size="sm" onClick={finalise}>
                  <FinaliseIcon aria-hidden className="me-1.5 size-4" />
                  Finalise invoice
                </Button>
              </div>
            ) : (
              <Text className="text-sm text-gray-600">A Super Admin finalises it.</Text>
            )}
          </div>
        </Alert>
      ) : null}

      {/* Only the open dialog is mounted: ReasonDialog's textarea id is fixed, so two mounted at once
          would share one id and the open one's label could point at a hidden field. */}
      {mayReview && act === "revise" ? (
        <ReasonDialog
          title={`Discount and round off on ${data.invoiceNumber}${data.orderNumber ? ` · order ${data.orderNumber}` : ""}`}
          consequence={
            <>
              Billing re-prices every line: the discount comes off <strong>before GST</strong>, so the tax is recharged on the
              discounted value. A rupee discount is split across the lines by their value. Leave the round off blank for the
              automatic one to the nearest rupee, or set your own within ₹1 either way.
            </>
          }
          action={handleRevise}
          confirmLabel="Re-price the invoice"
          error={adjustError}
          cancel={
            <Button type="button" variant="outline" onClick={closeAct}>
              Keep it as it is
            </Button>
          }
          reasonLabel="Why is it changing?"
          reasonHint="Recorded on the invoice's audit trail with the before and after totals."
        >
          <SelectField label="Discount" name="discountMode" defaultValue={data.discount ? "amount" : "none"}>
            <option value="none">No discount</option>
            <option value="percent">Percentage of every line (%)</option>
            <option value="amount">Rupees off the invoice (₹)</option>
          </SelectField>
          <Field
            label="Discount value"
            name="discountValue"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            defaultValue={data.discount ? String(data.discount) : ""}
            hint={`Now ${money(data.discount)} off a gross value of ${money(data.grossValue)}.`}
          />
          <Field
            label="Round off (₹)"
            name="roundOff"
            type="number"
            inputMode="decimal"
            min="-1"
            max="1"
            step="0.01"
            placeholder="Automatic"
            hint={`Now ${money(data.roundOff)}. Blank = to the nearest rupee.`}
          />
        </ReasonDialog>
      ) : null}

      {maySettle && act === "settle" ? (
        <ReasonDialog
          title={`Settle ${data.invoiceNumber}`}
          consequence={
            <>
              Write off what the customer did not pay. Both amounts come off the customer&rsquo;s ledger and go to Tally as a journal
              (Discount Allowed and Round Off). <strong>The invoice and its GST do not change</strong> — to reduce the GST itself,
              raise a credit note instead. One settlement per invoice.
            </>
          }
          action={handleSettle}
          confirmLabel="Record the settlement"
          error={adjustError}
          cancel={
            <Button type="button" variant="outline" onClick={closeAct}>
              Not now
            </Button>
          }
          reasonLabel="Why is it being settled?"
          reasonHint="e.g. the amount received and what was agreed. Recorded on the settlement and the audit trail."
        >
          <Field label="Settlement discount (₹)" name="discount" type="number" inputMode="decimal" min="0" step="0.01" defaultValue="" />
          <Field
            label="Round off (₹)"
            name="roundOff"
            type="number"
            inputMode="decimal"
            min="-1"
            max="1"
            step="0.01"
            defaultValue="0"
            hint="Within ₹1 either way — a larger amount is a discount."
          />
        </ReasonDialog>
      ) : null}

      {/* ── Flags ────────────────────────────────────────────────────────────────────────── */}
      {refused === "window-passed" ? (
        <div role="alert">
          <Alert color="warning" variant="flat" bar icon={<WarningIcon aria-hidden className="size-5" />}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <Text className="font-semibold text-orange-dark">
                  The cancellation window for this invoice has passed.{closedAt ? ` It closed on ${formatDate(closedAt)}.` : ""}
                </Text>
                <Text className="mt-0.5 text-sm">
                  {mayCancel ? "To correct it now, raise a credit note against it instead." : "Ask someone with credit-note access to raise a credit note against it."}
                </Text>
              </div>
              {mayCancel ? (
                <Link to={creditNoteHref}>
                  <Button as="span" size="sm" variant="outline" className="border-orange-dark text-orange-dark hover:bg-orange-dark hover:text-white">
                    <CreditNoteIcon aria-hidden className="me-1.5 size-4" />
                    Raise a credit note against {data.invoiceNumber}
                  </Button>
                </Link>
              ) : null}
            </div>
          </Alert>
        </div>
      ) : null}
      {cancelled ? (
        <Alert color="danger" variant="flat" bar icon={<CancelIcon aria-hidden className="size-5" />}>
          <Text className="font-semibold text-red-dark">This invoice is cancelled</Text>
          <Text className="mt-0.5 text-sm">It stays on the ledger marked cancelled — financial records are retained for eight years. The reason is in the activity below.</Text>
        </Alert>
      ) : null}
      {data.irnStatus === "failed" && !cancelled ? (
        <Alert color="warning" variant="flat" bar icon={<WarningIcon aria-hidden className="size-5" />}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Text className="font-semibold text-orange-dark">E-invoice registration failed</Text>
              <Text className="mt-0.5 text-sm">The portal answered and refused. Its reason, and the attempts so far, are on the registration screen.</Text>
            </div>
            {mayReadRegistration ? (
              <Link to={`${href}/registration`}>
                <Button as="span" size="sm" variant="outline" className="border-orange-dark text-orange-dark hover:bg-orange-dark hover:text-white">
                  Open registration
                </Button>
              </Link>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {cancel === "1" && mayCancel && !cancelled ? (
      <ReasonDialog
        title={`Cancel ${data.invoiceNumber}`}
        consequence={
          <>
            Cancellation is permitted only inside the configured window. The invoice stays on the ledger marked cancelled — it is not
            removed — and the reason is written to the immutable audit trail. Downstream, Tally receives the cancellation; a credit-note
            voucher is what a post-window correction produces instead.
          </>
        }
        action={handleCancel}
        confirmLabel="Cancel this invoice"
        error={dialogError}
        cancel={
          <Link to={href}>
            <Button as="span" variant="outline">
              Keep the invoice
            </Button>
          </Link>
        }
      />
      ) : null}

      {/* ── Figures ──────────────────────────────────────────────────────────────────────── */}
      <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
        <MetricCard
          title="Grand total"
          metric={data.grandTotalDisplay}
          icon={<ReceiptIcon aria-hidden className="size-6" />}
          iconClassName="bg-primary text-white shadow-sm"
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              Taxable value {money(data.taxableValue)}
              {data.discount ? <> · after {money(data.discount)} trade discount</> : null}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Tax head"
          metric={<span className="capitalize">{statusWords(data.taxHead)}</span>}
          icon={<ScalesIcon aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              {data.igst ? `IGST ${money(data.igst)}` : `CGST ${money(data.cgst)} · SGST ${money(data.sgst)}`}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Invoice lines"
          metric={`${data.lines.length} ${data.lines.length === 1 ? "product" : "products"}`}
          icon={<PackageIcon aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Place of supply {stateName(data.placeOfSupply)}</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="E-invoice"
          metric={data.irn ? "Registered" : <span className="capitalize">{statusWords(data.irnStatus)}</span>}
          icon={<SealIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={
            <Text className="mt-0.5 truncate text-xs text-gray-500" title={data.irn ?? undefined}>
              {data.irn ? `IRN ${data.irn.slice(0, 18)}…` : "No IRN on this invoice"}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </div>

      {/* ── Work area ────────────────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <div className="contents @5xl:col-span-8 @5xl:flex @5xl:flex-col @5xl:gap-6">
          <Tab className="rounded-xl border border-muted bg-gray-0 p-5 lg:p-7 dark:bg-gray-50">
            <Tab.List className="gap-6">
              <Tab.ListItem className="gap-2">
                <LinesIcon aria-hidden className="size-4" />
                Line items
              </Tab.ListItem>
              <Tab.ListItem className="gap-2">
                <ReceiptIcon aria-hidden className="size-4" />
                Tax invoice
              </Tab.ListItem>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel className="pt-5">
                <Lines invoice={data} />
              </Tab.Panel>
              <Tab.Panel className="pt-5">
                <InvoiceDocument data={{ ...data, placeOfSupply: stateName(data.placeOfSupply) }} cancelled={cancelled} irnState={irnState} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab>

          <div className="order-last">
            <TileBoundary
              resolve={audit}
              fallbackClassName="h-[280px]"
              errorFallback={(error) => (
                <Alert color="warning" variant="flat" icon={<WarningIcon aria-hidden className="size-5" />}>
                  <Text className="text-sm font-semibold">Could not read the audit trail</Text>
                  <Text className="mt-0.5 text-sm">{problemMessage(error, "Billing did not answer.")}</Text>
                </Alert>
              )}
            >
              {(entries) => <AuditFeed entries={entries} limit={6} allHref={`${href}/audit`} />}
            </TileBoundary>
          </div>
        </div>

        <div className="contents @5xl:col-span-4 @5xl:flex @5xl:flex-col @5xl:gap-6">
          <TotalsCard
            heading="What the customer owes"
            total={data.grandTotalDisplay}
            caption={
              <>
                {data.lines.length} {data.lines.length === 1 ? "line" : "lines"} · <span className="capitalize">{statusWords(data.taxHead)}</span>
              </>
            }
            doc={data}
            footer={
              cancelled ? (
                <div className="flex items-center gap-2 border-t border-muted bg-red-lighter/30 px-6 py-4 text-sm text-red-dark dark:bg-red-lighter/10">
                  <CancelIcon aria-hidden className="size-4" />
                  Cancelled — kept on the ledger
                </div>
              ) : null
            }
          />
          <TileBoundary
            resolve={settlement}
            fallbackClassName="h-[160px]"
            errorFallback={(error) => (
              <Alert color="warning" variant="flat" icon={<WarningIcon aria-hidden className="size-5" />}>
                <Text className="text-sm font-semibold">Could not read the settlement</Text>
                <Text className="mt-0.5 text-sm">{problemMessage(error, "Billing did not answer.")}</Text>
              </Alert>
            )}
          >
            {(recorded) =>
              recorded || maySettle ? (
                <SettlementCard settlement={recorded} onSettle={maySettle && !recorded ? () => navigate(`${href}?act=settle`) : undefined} />
              ) : null
            }
          </TileBoundary>
          <PartiesCard invoice={data} />
          <ComplianceCard invoice={data} invoiceKey={key} mayReadRegistration={mayReadRegistration} mayDispatch={mayDispatch && !cancelled} />
        </div>
      </div>
    </div>
  );
}

/* ── Source order ─────────────────────────────────────────────────────────────────────────── */

/**
 * The order the invoice was raised from, read-only: Billing's own orderId is the link, the number is
 * Ordering's label for it. Nothing here can re-point an invoice at another order.
 */
function OrderLink({ invoice }: { invoice: InvoiceDetail }) {
  if (!invoice.orderId) {
    return (
      <Text as="span" className="text-sm text-gray-500">
        No source order
      </Text>
    );
  }
  return (
    <Link to={orderPath(invoice.orderNumber || invoice.orderId)} className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:underline">
      <PackageIcon aria-hidden className="size-4" />
      Order <span className="font-semibold text-gray-900">{invoice.orderNumber ?? "(number unavailable)"}</span>
    </Link>
  );
}

/* ── Lines ────────────────────────────────────────────────────────────────────────────────── */

const AVATAR_COLORS = ["secondary", "info", "primary"] as const;

function Lines({ invoice }: { invoice: InvoiceDetail }) {
  // D-39: the gross and discount columns appear only on an invoice that carries a trade discount.
  const discounted = invoice.discount > 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="-mx-5 overflow-x-auto lg:-mx-7">
        <Table variant="modern" className={`w-full min-w-[680px] ${GRID_TABLE}`}>
          <caption className="sr-only">The lines on invoice {invoice.invoiceNumber}</caption>
          <Table.Header>
            <Table.Row className="hover:bg-transparent">
              <Table.Head className="ps-5 lg:ps-7">Product</Table.Head>
              <Table.Head className="text-right">Quantity</Table.Head>
              {discounted ? (
                <>
                  <Table.Head className="text-right">Amount</Table.Head>
                  <Table.Head className="text-right">Discount</Table.Head>
                </>
              ) : null}
              <Table.Head className="text-right">Taxable value</Table.Head>
              <Table.Head className="text-right">Tax</Table.Head>
              <Table.Head className="pe-5 text-right lg:pe-7">Line total</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {invoice.lines.map((line, index) => (
              <Table.Row key={line.id || index} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-100/40">
                <Table.Cell className="py-4 ps-5 lg:ps-7">
                  <div className="flex items-center gap-3">
                    <Avatar name={line.skuName} color={AVATAR_COLORS[index % AVATAR_COLORS.length]} rounded="md" size="md" />
                    <div className="min-w-0">
                      <Text className="line-clamp-2 font-medium text-gray-900">{line.skuName}</Text>
                      <Text className="text-xs text-gray-500">{[line.brandName, line.categoryName].filter(Boolean).join(" · ")}</Text>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" size="sm" rounded="md" className="border-muted font-mono text-[11px] text-gray-600">
                          HSN {line.hsnCode}
                        </Badge>
                        <Badge variant="flat" color="info" size="sm" rounded="md" className="text-[11px]">
                          GST {String(line.gstRate)}%
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="py-4 text-right">
                  <span className="font-medium tabular-nums text-gray-900">
                    {String(line.quantity)} <span className="text-xs text-gray-500">{line.unitCode}</span>
                  </span>
                </Table.Cell>
                {discounted ? (
                  <>
                    <Table.Cell className="py-4 text-right tabular-nums text-gray-700">{money(line.grossAmount)}</Table.Cell>
                    <Table.Cell className="py-4 text-right">
                      <span className="tabular-nums text-green-dark">−{money(line.discount)}</span>
                      {line.discountPercent ? <span className="block text-xs text-gray-500">{String(line.discountPercent)}%</span> : null}
                    </Table.Cell>
                  </>
                ) : null}
                <Table.Cell className="py-4 text-right tabular-nums text-gray-700">{money(line.taxableValue)}</Table.Cell>
                <Table.Cell className="py-4 text-right tabular-nums text-gray-700">{money(line.taxAmount)}</Table.Cell>
                <Table.Cell className="py-4 pe-5 text-right lg:pe-7">
                  <span className="font-semibold tabular-nums text-gray-900">{line.lineTotalDisplay}</span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      {BASIS_RULE[invoice.invoiceBasis] ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-s-4 border-muted border-s-secondary bg-secondary-lighter/30 px-4 py-3 text-sm text-gray-700 dark:bg-secondary-lighter/10">
          <InfoIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-secondary" />
          {BASIS_RULE[invoice.invoiceBasis]}
        </div>
      ) : null}
    </div>
  );
}

/* ── Settlement (D-41) ─────────────────────────────────────────────────────────────────────── */

function SettlementCard({ settlement, onSettle }: { settlement: Settlement | null; onSettle?: () => void }) {
  const row = (label: string, value: ReactNode) => (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium tabular-nums text-gray-900">{value}</dd>
    </div>
  );

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<SettleIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
          Settlement
        </CardTitle>
      }
      headerClassName="items-center"
    >
      {settlement ? (
        <div className="mt-5 flex flex-col gap-3">
          <dl className="flex flex-col gap-2.5">
            {row("Settlement discount", money(settlement.discount))}
            {row("Round off", money(settlement.roundOff))}
            <div className="border-t border-dashed border-muted" />
            {row("Written off the ledger", <span className="text-green-dark">−{money(settlement.total)}</span>)}
            {row("Settled on", formatDate(settlement.settledOn))}
            {row("Settled by", settlement.settledByName ?? "Unknown user")}
          </dl>
          <Text className="rounded-md border-s-2 border-muted bg-gray-50 px-3 py-2 text-sm italic text-gray-700 dark:bg-gray-100/40">
            “{settlement.reason}”
          </Text>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="flat" color={settlement.ledgerPosted ? "success" : "warning"} rounded="pill" size="sm">
              {settlement.ledgerPosted ? "On the ledger" : "Ledger post retained — retrying"}
            </Badge>
            <Badge variant="flat" color={settlement.accountingQueued ? "success" : "warning"} rounded="pill" size="sm">
              {settlement.accountingQueued ? "Queued for Tally" : "Tally offer retained — retrying"}
            </Badge>
          </div>
          <Text className="text-xs text-gray-500">The invoice and its GST are unchanged.</Text>
        </div>
      ) : (
        <div className="pt-5">
          <EmptyState
            title="Not settled"
            action={
              onSettle ? (
                <Button type="button" size="sm" onClick={onSettle}>
                  <SettleIcon aria-hidden className="me-1.5 size-4" />
                  Record settlement…
                </Button>
              ) : null
            }
          >
            When the customer has paid, write off any agreed discount and round off here so the ledger closes at zero.
          </EmptyState>
        </div>
      )}
    </WidgetCard>
  );
}

/* ── Parties ──────────────────────────────────────────────────────────────────────────────── */

function PartiesCard({ invoice }: { invoice: InvoiceDetail }) {
  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<PartiesIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
          Parties
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <div className="mt-5 flex flex-col gap-5">
        <PartyBlock
          label="Billed to"
          tile="bg-blue-lighter text-blue-dark"
          name={invoice.recipientLegalName}
          gstin={invoice.recipientGstin}
          noGstin="No GSTIN — unregistered recipient"
          address={invoice.recipientAddress}
        />
        <div className="border-t border-dashed border-muted" />
        <PartyBlock
          label="Supplier"
          tile="bg-secondary-lighter text-secondary-dark"
          name={invoice.supplierLegalName}
          gstin={invoice.supplierGstin}
          noGstin="GSTIN not recorded — awaiting the company GSTIN"
          address={invoice.supplierAddress}
        />
        <InfoBlock icon={<MapPinIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Place of supply">
          <Text className="text-sm text-gray-900">{stateName(invoice.placeOfSupply)}</Text>
        </InfoBlock>
      </div>
    </WidgetCard>
  );
}

/* ── Compliance ───────────────────────────────────────────────────────────────────────────── */

function ComplianceCard({ invoice, invoiceKey, mayReadRegistration, mayDispatch }: { invoice: InvoiceDetail; invoiceKey: string; mayReadRegistration: boolean; mayDispatch: boolean }) {
  const href = invoicePath(invoiceKey);
  const linkClass = "h-auto px-1.5 py-0.5 text-secondary";
  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<ShieldIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
          Compliance
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <div className="mt-5 flex flex-col gap-5">
        <InfoBlock
          icon={<SealIcon aria-hidden className="size-4" />}
          tile="bg-green-lighter text-green-dark"
          label="E-invoice (IRN)"
          action={
            mayReadRegistration ? (
              <Link to={`${href}/registration`}>
                <Button as="span" variant="text" size="sm" className={linkClass}>
                  Manage
                </Button>
              </Link>
            ) : null
          }
        >
          <div className="mt-1">
            <ComplianceBadge subject="irn" status={invoice.irnStatus} map={IRN_STATE} />
          </div>
          {invoice.irn ? (
            <span className="mt-2 flex items-center gap-1">
              <Text as="span" className="min-w-0 break-all font-mono text-xs text-gray-700">
                {invoice.irn}
              </Text>
              <CopyButton value={invoice.irn} label="Copy the IRN" />
            </span>
          ) : null}
        </InfoBlock>

        <div className="border-t border-dashed border-muted" />

        <InfoBlock
          icon={<TruckIcon aria-hidden className="size-4" />}
          tile="bg-blue-lighter text-blue-dark"
          label="E-way bill"
          action={
            mayDispatch ? (
              <Link to={dispatchPath(invoiceKey)}>
                <Button as="span" variant="text" size="sm" className={linkClass}>
                  Dispatch
                </Button>
              </Link>
            ) : null
          }
        >
          {invoice.ewayBillNo || invoice.ewayBillStatus ? (
            <div className="flex flex-col gap-1.5">
              {invoice.ewayBillStatus ? <div>{getStatusBadge(invoice.ewayBillStatus)}</div> : null}
              {invoice.ewayBillNo ? <Text className="font-mono text-sm text-gray-900">{invoice.ewayBillNo}</Text> : null}
              {invoice.ewayBillDate || invoice.ewayBillValidUntil ? (
                <Text className="text-xs text-gray-500">
                  {invoice.ewayBillDate ? `Generated ${formatDate(invoice.ewayBillDate)}` : null}
                  {invoice.ewayBillDate && invoice.ewayBillValidUntil ? " · " : null}
                  {invoice.ewayBillValidUntil ? `valid until ${formatDate(invoice.ewayBillValidUntil)}` : null}
                </Text>
              ) : null}
            </div>
          ) : (
            <Text className="text-sm text-gray-500">None recorded — none was attempted.</Text>
          )}
        </InfoBlock>
      </div>
    </WidgetCard>
  );
}
